/**
 * Authenticated account API client.
 * Identity comes only from the Firebase token. Never sends a browser UID.
 */

import { ACCOUNT_PATHS, billingWebConfig, isBillingApiConfigured } from "../billing/billingConfig";
import { getAppCheckToken, getIdToken } from "../billing/firebaseAuth";
import {
  captureScope,
  createScopedAbortController,
  isCurrentScope,
} from "../auth/authSession";

function apiError(message, extra = {}) {
  const error = new Error(message);
  Object.assign(error, extra);
  return error;
}

async function parseJsonSafe(response) {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

export async function accountRequest(path, options = {}) {
  if (!isBillingApiConfigured()) {
    throw apiError("billing_api_unconfigured", { code: "billing_api_unconfigured" });
  }
  const scope = options.scope || captureScope();
  const controller = createScopedAbortController();
  const timeoutMs = options.timeoutMs ?? 20000;
  const timeout = setTimeout(() => {
    try {
      controller.abort();
    } catch {
      /* ignore */
    }
  }, timeoutMs);
  if (options.signal) {
    if (options.signal.aborted) controller.abort();
    else {
      options.signal.addEventListener("abort", () => controller.abort(), { once: true });
    }
  }
  const signal = controller.signal;

  const headers = {
    "Content-Type": "application/json",
    ...(options.headers || {}),
  };

  const send = async (forceRefresh) => {
    const token = await getIdToken({ forceRefresh });
    if (!token) throw apiError("signed_out", { code: "signed_out", status: 401 });
    headers.Authorization = `Bearer ${token}`;
    const appCheck = await getAppCheckToken();
    if (appCheck) headers["X-Firebase-AppCheck"] = appCheck;
    if (options.idempotencyKey) headers["Idempotency-Key"] = options.idempotencyKey;
    return fetch(`${billingWebConfig.billingApiBaseUrl}${path}`, {
      method: options.method || "GET",
      headers,
      credentials: "omit",
      cache: "no-store",
      signal,
      body: options.body ? JSON.stringify(options.body) : undefined,
    });
  };

  try {
    let response = await send(false);
    if (response.status === 401 && options.retryAuth !== false) {
      response = await send(true);
    }
    if (!isCurrentScope(scope)) {
      throw apiError("stale_session", { code: "stale_session" });
    }
    if (options.raw) return { response, scope };
    const json = await parseJsonSafe(response);
    if (!isCurrentScope(scope)) {
      throw apiError("stale_session", { code: "stale_session" });
    }
    if (!response.ok) {
      throw apiError(json?.error?.message || "request_failed", {
        code: json?.error?.code || `http_${response.status}`,
        status: response.status,
        details: json?.error || json,
      });
    }
    return json;
  } catch (err) {
    if (err?.code === "stale_session" || err?.code === "signed_out") throw err;
    if (err?.name === "AbortError") {
      throw apiError("request_aborted", { code: "request_aborted", abort: true });
    }
    throw err;
  } finally {
    clearTimeout(timeout);
  }
}

export async function fetchLifecycle(options = {}) {
  const json = await accountRequest(ACCOUNT_PATHS.lifecycle, options);
  return json?.lifecycle || json;
}

export function registerAccount({ commandId, consent, scope } = {}) {
  return accountRequest(ACCOUNT_PATHS.register, {
    method: "POST",
    body: {
      ...(commandId ? { commandId } : {}),
      ...(consent ? { consent } : {}),
    },
    idempotencyKey: commandId,
    scope,
  });
}

export function fetchProfile(options = {}) {
  return accountRequest(ACCOUNT_PATHS.profile, options);
}

export function patchProfile({ displayName, expectedRevision, scope }) {
  return accountRequest(ACCOUNT_PATHS.profile, {
    method: "PATCH",
    body: { displayName, expectedRevision },
    scope,
  });
}

export function fetchUsage(options = {}) {
  return accountRequest(ACCOUNT_PATHS.usage, options);
}

export function fetchPreferences(options = {}) {
  return accountRequest(ACCOUNT_PATHS.preferences, options);
}

export function patchPreferences(body, options = {}) {
  return accountRequest(ACCOUNT_PATHS.preferences, {
    method: "PATCH",
    body,
    ...options,
  });
}

export async function downloadAccountExport(options = {}) {
  const { response, scope } = await accountRequest(ACCOUNT_PATHS.export, {
    ...options,
    raw: true,
  });
  if (!isCurrentScope(scope)) throw apiError("stale_session", { code: "stale_session" });
  const blob = await response.blob();
  if (!response.ok) {
    throw apiError("export_failed", { code: "export_failed", status: response.status });
  }
  return blob;
}

export function requestAccountDeletion({ idempotencyKey, scope }) {
  return accountRequest(ACCOUNT_PATHS.deletion, {
    method: "POST",
    body: { idempotencyKey },
    idempotencyKey,
    scope,
  });
}

export function fetchDeletionStatus(jobId, options = {}) {
  return accountRequest(`${ACCOUNT_PATHS.deletion}/${encodeURIComponent(jobId)}`, options);
}

export async function fetchDeletionReceipt(capability) {
  if (!isBillingApiConfigured()) {
    throw apiError("billing_api_unconfigured", { code: "billing_api_unconfigured" });
  }
  const url = new URL(
    `${billingWebConfig.billingApiBaseUrl}${ACCOUNT_PATHS.deletionReceipt}`
  );
  const response = await fetch(url.toString(), {
    method: "GET",
    credentials: "omit",
    cache: "no-store",
    headers: { "X-Deletion-Receipt": capability },
  });
  const json = await parseJsonSafe(response);
  if (!response.ok) {
    throw apiError(json?.error?.message || "receipt_failed", {
      code: json?.error?.code || `http_${response.status}`,
      status: response.status,
    });
  }
  return json;
}
